import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ClipboardList,
  Layers,
  Send,
  Play,
  CheckCircle,
  Archive,
  RefreshCw,
  Loader2,
  Building2,
  Calendar,
  AlertTriangle,
  Clock,
  Eye,
  Plus,
  Edit,
  Trash2,
  MessageSquare,
  GripVertical,
  ArrowRight,
  ChevronRight,
  X,
  Activity,
  CheckSquare,
  Square,
  Paperclip,
  Image as ImageIcon,
  Flag,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TicketChat from "@/components/TicketChat";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ---------------------------------------------------------------------------
// Live progress helpers — pull from /api/hospital/maintenance-requests/{id}
// which already returns a hospital-safe doc with checklist, completion_pct,
// and only external-visibility updates.
// ---------------------------------------------------------------------------
function computeCompletionPct(item) {
  if (typeof item?.completion_pct === "number") return item.completion_pct;
  const list = item?.checklist || [];
  if (!list.length) return 0;
  const done = list.filter((t) => t.completed).length;
  return Math.round((done / list.length) * 100);
}

function UpdateTypeIcon({ type }) {
  const cls = "w-3.5 h-3.5";
  if (type === "completion") return <CheckCircle className={`${cls} text-emerald-600`} />;
  if (type === "milestone") return <Flag className={`${cls} text-violet-600`} />;
  if (type === "supplier_quote" || type === "supplier_invoice")
    return <Paperclip className={`${cls} text-amber-600`} />;
  return <Activity className={`${cls} text-blue-600`} />;
}

// Live progress section — shown inside the hospital detail modal.
// Re-fetches the hospital-safe doc whenever the modal opens.
function HospitalLiveProgress({ requestId, token, fallback }) {
  const [doc, setDoc] = useState(fallback || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    title: "",
    description: "",
    occurred_at: new Date().toISOString().slice(0, 16),
  });
  const [savingIncident, setSavingIncident] = useState(false);

  const refresh = async () => {
    if (!requestId || !token) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(
        `${API}/hospital/maintenance-requests/${requestId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDoc(r.data);
    } catch (e) {
      // Fall back to the cached pipeline row if the lifecycle endpoint refuses
      // (e.g. WO created by another user on the same health system).
      setErr(e?.response?.data?.detail || "Couldn't load live progress");
      if (!doc && fallback) setDoc(fallback);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, token]);

  if (loading && !doc) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="text-sm text-slate-500 italic">
        {err || "No live progress data yet."}
      </div>
    );
  }

  const pct = computeCompletionPct(doc);
  const checklist = doc.checklist || [];
  const updates = (doc.updates || []).slice().sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
  const signoff = doc.signoff_decision;
  const signoffSummary = doc.signoff_summary;

  return (
    <div className="space-y-4" data-testid="hospital-wo-live-progress">
      {/* Refresh + status banner */}
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5 text-blue-500" />
          Live progress
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 disabled:opacity-50"
          disabled={loading}
          data-testid="hospital-wo-refresh-progress"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Status pill for awaiting sign-off / closed / disputed */}
      {(doc.status === "awaiting_hospital_signoff" || signoff) && (
        <div
          className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${
            signoff === "approve"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : signoff === "dispute"
              ? "border-rose-200 bg-rose-50 text-rose-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
          data-testid="hospital-wo-signoff-banner"
        >
          <Flag className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-semibold">
              {signoff === "approve"
                ? "You approved this work on " +
                  (doc.signoff_decided_at
                    ? new Date(doc.signoff_decided_at).toLocaleDateString()
                    : "")
                : signoff === "dispute"
                ? "Sign-off disputed — Sterile Mate has been notified"
                : "Awaiting your sign-off"}
            </div>
            {signoffSummary && (
              <div className="text-xs mt-1 opacity-90">{signoffSummary}</div>
            )}
          </div>
        </div>
      )}

      {/* Completion bar */}
      <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="hospital-wo-progress-bar">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-slate-700">
            Completion {checklist.length > 0 && (
              <span className="text-slate-400 font-normal">
                · {checklist.filter((t) => t.completed).length} of {checklist.length} tasks
              </span>
            )}
          </span>
          <span className="text-sm font-bold text-blue-600 tabular-nums" data-testid="hospital-wo-pct">
            {pct}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              pct === 100 ? "bg-emerald-500" : "bg-blue-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Checklist (read-only for hospital) */}
      {checklist.length > 0 && (
        <div data-testid="hospital-wo-checklist">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Field checklist
          </div>
          <ul className="space-y-1.5">
            {checklist.map((t) => (
              <li
                key={t.id}
                className={`flex items-start gap-2 text-sm rounded-md px-2 py-1.5 ${
                  t.completed ? "bg-emerald-50/60" : "bg-slate-50"
                }`}
                data-testid={`hospital-wo-task-${t.id}`}
              >
                {t.completed ? (
                  <CheckSquare className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <Square className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className={`${
                      t.completed ? "line-through text-slate-500" : "text-slate-800"
                    }`}
                  >
                    {t.label}
                  </div>
                  {t.completed && t.completed_at && (
                    <div className="text-[10px] text-slate-400">
                      Completed {new Date(t.completed_at).toLocaleString()}
                      {t.completed_by && ` · ${t.completed_by}`}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Updates feed (filtered to external-only by backend) */}
      <div data-testid="hospital-wo-updates">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          Field updates · {updates.length}
        </div>
        {updates.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            No public updates yet. The team will post here as work progresses.
          </div>
        ) : (
          <ol className="relative border-l-2 border-slate-100 ml-1 space-y-3 pl-3">
            {updates.map((u) => (
              <li key={u.id} className="relative" data-testid={`hospital-wo-update-${u.id}`}>
                <span className="absolute -left-[19px] top-0.5 w-3 h-3 rounded-full bg-white border-2 border-blue-400" />
                <div className="flex items-start gap-2">
                  <UpdateTypeIcon type={u.update_type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-700">
                        {u.by_name || "Sterile Mate"}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleString()
                          : ""}
                      </span>
                      {u.update_type && u.update_type !== "progress" && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {u.update_type.replace("_", " ")}
                        </span>
                      )}
                    </div>
                    {u.message && (
                      <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words">
                        {u.message}
                      </p>
                    )}
                    {/* Photos */}
                    {Array.isArray(u.photos) && u.photos.length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {u.photos.map((p, i) => (
                          <a
                            key={i}
                            href={
                              p.startsWith("http")
                                ? p
                                : `${process.env.REACT_APP_BACKEND_URL}${p}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                          >
                            <img
                              src={
                                p.startsWith("http")
                                  ? p
                                  : `${process.env.REACT_APP_BACKEND_URL}${p}`
                              }
                              alt=""
                              className="w-14 h-14 object-cover rounded border border-slate-200 hover:opacity-80"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {/* Attachments */}
                    {Array.isArray(u.attachments) && u.attachments.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {u.attachments.map((a, i) => (
                          <li key={i}>
                            <a
                              href={
                                a.file_url?.startsWith("http")
                                  ? a.file_url
                                  : `${process.env.REACT_APP_BACKEND_URL}${a.file_url || ""}`
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              {a.file_name || "Attachment"}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Report an incident — hospital-side */}
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3" data-testid="hospital-wo-incident-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Flag className="w-3.5 h-3.5 text-rose-500" />
            <div>
              <div className="text-xs font-semibold text-slate-800">Spot a problem?</div>
              <div className="text-[11px] text-slate-500">Log an incident — admin and the contractor are notified instantly.</div>
            </div>
          </div>
          {!showIncidentForm && (
            <button
              type="button"
              onClick={() => setShowIncidentForm(true)}
              className="inline-flex items-center gap-1 h-7 px-2 rounded border border-rose-300 bg-white text-xs font-medium text-rose-700 hover:bg-rose-50"
              data-testid="hospital-wo-incident-toggle"
            >
              <Plus className="w-3 h-3" /> Report incident
            </button>
          )}
        </div>
        {showIncidentForm && (
          <div className="mt-3 space-y-2 border-t border-rose-100 pt-3">
            <input
              type="text"
              value={incidentForm.title}
              onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
              placeholder="Short title (e.g. Loud banging during HVAC test)"
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="hospital-wo-incident-title"
            />
            <input
              type="datetime-local"
              value={incidentForm.occurred_at}
              onChange={(e) => setIncidentForm({ ...incidentForm, occurred_at: e.target.value })}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
            />
            <textarea
              rows={3}
              value={incidentForm.description}
              onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
              placeholder="What happened? Who or what is affected?"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
              data-testid="hospital-wo-incident-description"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowIncidentForm(false)}
                className="h-7 px-3 rounded border border-slate-300 bg-white text-xs hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!incidentForm.title.trim()) {
                    toast.error("Add a title");
                    return;
                  }
                  setSavingIncident(true);
                  try {
                    const r = await axios.post(
                      `${API}/hospital/work-orders/${requestId}/incidents`,
                      {
                        title: incidentForm.title.trim(),
                        description: incidentForm.description.trim() || undefined,
                        occurred_at: incidentForm.occurred_at
                          ? new Date(incidentForm.occurred_at).toISOString()
                          : undefined,
                      },
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    toast.success("Incident logged — admin + contractor notified");
                    setShowIncidentForm(false);
                    setIncidentForm({
                      title: "",
                      description: "",
                      occurred_at: new Date().toISOString().slice(0, 16),
                    });
                    if (r.data?.public_url) {
                      window.open(r.data.public_url, "_blank");
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to log incident");
                  } finally {
                    setSavingIncident(false);
                  }
                }}
                disabled={savingIncident}
                className="h-7 px-3 rounded bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:opacity-50"
                data-testid="hospital-wo-incident-submit"
              >
                {savingIncident ? "Sending…" : "Submit & notify"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
const STAGES = [
  { key: "intake", label: "Intake", icon: ClipboardList, color: "bg-slate-500" },
  { key: "triage", label: "Triage", icon: Layers, color: "bg-amber-500" },
  { key: "dispatch", label: "Dispatch", icon: Send, color: "bg-blue-500" },
  { key: "in_progress", label: "In Progress", icon: Play, color: "bg-purple-500" },
  { key: "completion", label: "Completion", icon: CheckCircle, color: "bg-emerald-500" },
  { key: "closed", label: "Closed", icon: Archive, color: "bg-slate-600" }
];

// Request types
const REQUEST_TYPES = [
  { id: "water_leak", label: "Water Leak", icon: "💧", priority: "high" },
  { id: "equipment_malfunction", label: "Equipment Malfunction", icon: "⚙️", priority: "high" },
  { id: "hvac_issue", label: "HVAC Issue", icon: "❄️", priority: "normal" },
  { id: "plumbing", label: "Plumbing Issue", icon: "🔧", priority: "normal" },
  { id: "electrical", label: "Electrical Issue", icon: "⚡", priority: "high" },
  { id: "filter_replacement", label: "Filter Replacement", icon: "🔄", priority: "low" },
  { id: "preventive_maintenance", label: "Preventive Maintenance", icon: "📋", priority: "low" },
  { id: "other", label: "Other", icon: "📝", priority: "normal" }
];

// Draggable Card Component
function DraggableCard({ item, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing bg-blue-50 hover:bg-blue-100 rounded-l-lg border-r border-blue-200 z-20 touch-none"
        onClick={(e) => e.stopPropagation()}
        title="Drag to move"
      >
        <GripVertical className="w-5 h-5 text-blue-500" />
      </div>
      <div className="pl-8">
        {children}
      </div>
    </div>
  );
}

// Droppable Stage Column
function DroppableStage({ stageKey, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: stageKey,
    data: { type: 'stage', stageKey }
  });

  return (
    <div 
      ref={setNodeRef}
      data-stage={stageKey}
      className={`bg-slate-100 rounded-b-lg p-2 min-h-[400px] space-y-2 transition-colors ${
        isOver ? 'bg-blue-100 ring-2 ring-blue-300' : ''
      }`}
    >
      {children}
    </div>
  );
}

export default function HospitalMaintenancePipeline({ 
  token, 
  healthSystem, 
  user, 
  onNewRequest 
}) {
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("pipeline"); // pipeline or table
  const [activeId, setActiveId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState({
    request_type: "",
    priority: "",
    description: "",
    location_details: ""
  });

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  // Fetch maintenance requests
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hospital/maintenance-pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Organize requests by stage
      const requests = response.data.requests || [];
      const organizedPipeline = {};
      
      STAGES.forEach(stage => {
        organizedPipeline[stage.key] = requests.filter(
          r => (r.stage || "intake") === stage.key
        );
      });
      
      setPipeline(organizedPipeline);
    } catch (error) {
      console.error("Failed to fetch pipeline:", error);
      toast.error("Failed to load maintenance pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  // Quick Stage Change
  const handleQuickStageChange = async (item, newStage) => {
    // Optimistic UI update
    setPipeline(prev => {
      const oldStage = item.stage || "intake";
      const newPipeline = { ...prev };
      
      newPipeline[oldStage] = (newPipeline[oldStage] || []).filter(i => i.id !== item.id);
      const updatedItem = { ...item, stage: newStage };
      newPipeline[newStage] = [updatedItem, ...(newPipeline[newStage] || [])];
      
      return newPipeline;
    });
    
    try {
      await axios.put(
        `${API}/hospital/maintenance-request/${item.id}/stage`,
        { stage: newStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const stageLabel = STAGES.find(s => s.key === newStage)?.label || newStage;
      toast.success(`Moved to ${stageLabel}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to change stage");
      fetchData();
    }
  };

  // Get next stage
  const getNextStage = (currentStage) => {
    const stageOrder = ["intake", "triage", "dispatch", "in_progress", "completion", "closed"];
    const currentIndex = stageOrder.indexOf(currentStage);
    if (currentIndex >= 0 && currentIndex < stageOrder.length - 1) {
      return stageOrder[currentIndex + 1];
    }
    return null;
  };

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over) return;
    
    const draggedItemId = active.id;
    let targetStage = over.id;
    
    // Find the item being dragged
    let draggedItem = null;
    let currentStage = null;
    
    for (const [stage, items] of Object.entries(pipeline)) {
      const found = items.find(item => item.id === draggedItemId);
      if (found) {
        draggedItem = found;
        currentStage = stage;
        break;
      }
    }
    
    if (!draggedItem) return;
    
    const validStages = STAGES.map(s => s.key);
    
    if (!validStages.includes(targetStage)) {
      for (const [stage, items] of Object.entries(pipeline)) {
        const found = items.find(item => item.id === targetStage);
        if (found) {
          targetStage = stage;
          break;
        }
      }
    }
    
    if (!validStages.includes(targetStage) || currentStage === targetStage) return;
    
    await handleQuickStageChange(draggedItem, targetStage);
  };

  // Edit request
  const handleEditRequest = async () => {
    if (!selectedItem) return;
    
    try {
      await axios.put(
        `${API}/hospital/maintenance-request/${selectedItem.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Request updated successfully");
      setShowEditModal(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  // Cancel request
  const handleCancelRequest = async () => {
    if (!selectedItem) return;
    
    try {
      await axios.delete(
        `${API}/hospital/maintenance-request/${selectedItem.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Request cancelled");
      setShowDeleteConfirm(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel request");
    }
  };

  // Open edit modal
  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditForm({
      request_type: item.request_type || "",
      priority: item.priority || "normal",
      description: item.description || "",
      location_details: item.location_details || ""
    });
    setShowEditModal(true);
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700";
      case "normal": return "bg-blue-100 text-blue-700";
      case "low": return "bg-slate-100 text-slate-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  // Open the hospital's own quote PDF (token-gated route — never the admin one)
  const openQuotePdf = async (requestId) => {
    try {
      const r = await axios.get(
        `${API}/hospital/work-orders/${requestId}/customer-quote/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      window.open(window.URL.createObjectURL(r.data), "_blank", "noopener");
    } catch {
      alert("That quote isn't available yet. Please contact your Sterile Mate rep.");
    }
  };

  // Pipeline Card
  const PipelineCard = ({ item }) => {
    const currentStage = item.stage || "intake";
    const nextStage = getNextStage(currentStage);
    
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-3 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {REQUEST_TYPES.find(t => t.id === item.request_type)?.icon || "📝"}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(item.priority)}`}>
              {item.priority?.toUpperCase()}
            </span>
            {item.customer_quote?.sent_at && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openQuotePdf(item.id); }}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                title="View your service quote PDF"
                data-testid={`hospital-wo-quote-badge-${item.id}`}
              >
                💰 Quote ${Number(item.customer_quote?.totals?.total || 0).toLocaleString()}
              </button>
            )}
          </div>
          <span className="text-xs text-slate-400 font-mono">{item.id?.slice(0, 8)}</span>
        </div>
        
        <p className="font-medium text-slate-900 text-sm mb-1">
          {REQUEST_TYPES.find(t => t.id === item.request_type)?.label || item.request_type?.replace("_", " ")}
        </p>
        
        {item.location_details && (
          <p className="text-xs text-slate-500 mb-2 line-clamp-1">
            📍 {item.location_details}
          </p>
        )}
        
        {item.description && (
          <p className="text-xs text-slate-600 mb-2 line-clamp-2">{item.description}</p>
        )}

        {/* Live progress bar — only when there is a checklist or any progress */}
        {(item.checklist?.length > 0 || (item.updates?.length || 0) > 0) && (() => {
          const pct = computeCompletionPct(item);
          const updCount = (item.updates || []).filter(
            (u) => (u.visibility || "external") !== "internal",
          ).length;
          return (
            <div className="mb-2" data-testid={`hospital-wo-card-progress-${item.id}`}>
              <div className="flex items-center justify-between text-[10px] font-semibold text-slate-500 mb-1">
                <span className="flex items-center gap-1">
                  <Activity className="w-2.5 h-2.5 text-blue-500" />
                  {pct}% complete
                </span>
                {updCount > 0 && (
                  <span className="text-blue-600">{updCount} update{updCount === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}
        
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-400">
            {new Date(item.created_at).toLocaleDateString()}
          </span>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowDetailModal(true); }}
              className="h-7 px-2"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </Button>
            
            {["intake", "triage"].includes(currentStage) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                className="h-7 px-2"
              >
                <Edit className="w-3.5 h-3.5 text-blue-500" />
              </Button>
            )}
            
            {nextStage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleQuickStageChange(item, nextStage); }}
                className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                title={`Move to ${STAGES.find(s => s.key === nextStage)?.label}`}
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Find the active item for drag overlay
  const findActiveItem = () => {
    if (!activeId) return null;
    for (const items of Object.values(pipeline)) {
      const found = items.find(item => item.id === activeId);
      if (found) return found;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Maintenance Pipeline</h2>
          <p className="text-sm text-slate-500">Drag tickets between stages or use the arrow to advance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {onNewRequest && (
            <Button onClick={onNewRequest} className="bg-blue-600 hover:bg-blue-700" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          )}
        </div>
      </div>

      {/* Pipeline View */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-6 gap-3 overflow-x-auto">
          {STAGES.map((stage) => {
            const StageIcon = stage.icon;
            const items = pipeline[stage.key] || [];
            
            return (
              <div key={stage.key} className="min-w-[200px]">
                {/* Stage Header */}
                <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <StageIcon className="w-4 h-4" />
                    <span className="font-medium text-sm">{stage.label}</span>
                  </div>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-bold">
                    {items.length}
                  </span>
                </div>
                
                {/* Stage Column */}
                <DroppableStage stageKey={stage.key}>
                  <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {items.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        No tickets
                      </div>
                    ) : (
                      items.map((item) => (
                        <DraggableCard key={item.id} item={item}>
                          <PipelineCard item={item} />
                        </DraggableCard>
                      ))
                    )}
                  </SortableContext>
                </DroppableStage>
              </div>
            );
          })}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId ? (
            <div className="opacity-80">
              <PipelineCard item={findActiveItem()} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Detail Modal with Chat */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-lg">
                {REQUEST_TYPES.find(t => t.id === selectedItem?.request_type)?.icon || "📝"}
              </span>
              {REQUEST_TYPES.find(t => t.id === selectedItem?.request_type)?.label || selectedItem?.request_type}
            </DialogTitle>
            <DialogDescription>
              ID: {selectedItem?.id} • {selectedItem?.stage?.replace("_", " ").toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">Priority</Label>
                  <p className="font-medium">{selectedItem.priority?.toUpperCase()}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Created</Label>
                  <p className="font-medium">{new Date(selectedItem.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              
              {selectedItem.location_details && (
                <div>
                  <Label className="text-slate-500 text-xs">Location</Label>
                  <p>{selectedItem.location_details}</p>
                </div>
              )}
              
              {selectedItem.description && (
                <div>
                  <Label className="text-slate-500 text-xs">Description</Label>
                  <p className="text-sm">{selectedItem.description}</p>
                </div>
              )}

              {/* Live progress — checklist + completion + field updates feed */}
              <div className="border-t pt-4">
                <HospitalLiveProgress
                  requestId={selectedItem.id}
                  token={token}
                  fallback={selectedItem}
                />
              </div>

              {/* Chat Section */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Communication</h4>
                <TicketChat 
                  ticketId={selectedItem.id}
                  ticketType="maintenance"
                  currentUser={user}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Request</DialogTitle>
            <DialogDescription>
              Update the maintenance request details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Request Type</Label>
              <Select 
                value={editForm.request_type}
                onValueChange={(v) => setEditForm({...editForm, request_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Priority</Label>
              <Select 
                value={editForm.priority}
                onValueChange={(v) => setEditForm({...editForm, priority: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Location</Label>
              <Input
                value={editForm.location_details}
                onChange={(e) => setEditForm({...editForm, location_details: e.target.value})}
                placeholder="Building, floor, room..."
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                placeholder="Describe the issue..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditRequest}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Request?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this maintenance request? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Keep Request
            </Button>
            <Button variant="destructive" onClick={handleCancelRequest}>
              Cancel Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
